/**
 * CategoryWorkspacePanel - Multi-track workspace showing categories at different maturity stages
 *
 * Content Managers work on multiple categories simultaneously, each at different stages.
 * This panel shows active work across categories with visual maturity indicators.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Layers,
  Sparkles,
  FileText,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  TrendingUp,
  Package,
  Eye,
  PenTool
} from 'lucide-react';

// Maturity stages for a category
export type MaturityStage = 'early' | 'enriched' | 'content_ready' | 'published' | 'optimized';

export interface CategoryWorkItem {
  id: string;
  name: string;
  path: string; // Full category path (e.g., "Cycling > Components > Wheels")
  stage: MaturityStage;
  metrics: {
    totalProducts: number;
    enrichedProducts: number;
    enrichmentPercentage: number;
    hasAEOContent: boolean;
    hasQuestionWheel: boolean;
    pendingReviews: number;
    searchVolume?: number;
    demandScore?: number;
  };
  lastActivity?: string;
  suggestedAction?: {
    label: string;
    path: string;
    priority: 'high' | 'medium' | 'low';
  };
}

interface CategoryWorkspacePanelProps {
  categories: CategoryWorkItem[];
  loading?: boolean;
  onRefresh?: () => void;
}

const stageConfig: Record<MaturityStage, {
  label: string;
  color: string;
  bgColor: string;
  icon: React.ElementType;
  description: string;
}> = {
  early: {
    label: 'Early Stage',
    color: 'text-gray-600',
    bgColor: 'bg-gray-100',
    icon: Package,
    description: 'Products imported, needs enrichment'
  },
  enriched: {
    label: 'Enriched',
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
    icon: Sparkles,
    description: 'AI enrichment complete, ready for content'
  },
  content_ready: {
    label: 'Content Ready',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    icon: FileText,
    description: 'AEO content created, needs review'
  },
  published: {
    label: 'Published',
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    icon: CheckCircle2,
    description: 'Content live, monitoring performance'
  },
  optimized: {
    label: 'Optimized',
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
    icon: TrendingUp,
    description: 'Continuously improving based on data'
  }
};

function getStageProgress(stage: MaturityStage): number {
  const progressMap: Record<MaturityStage, number> = {
    early: 20,
    enriched: 45,
    content_ready: 70,
    published: 90,
    optimized: 100
  };
  return progressMap[stage];
}

export function CategoryWorkspacePanel({ categories, loading, onRefresh }: CategoryWorkspacePanelProps) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-indigo-500" />
            My Active Work
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <div className="animate-pulse text-muted-foreground">Loading workspace...</div>
        </CardContent>
      </Card>
    );
  }

  if (!categories || categories.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="flex flex-col items-center justify-center py-8">
          <Layers className="h-10 w-10 text-muted-foreground mb-3" />
          <h4 className="font-semibold mb-1">No Active Categories</h4>
          <p className="text-sm text-muted-foreground text-center mb-4">
            Start by uploading products or selecting categories to work on.
          </p>
          <Button onClick={() => navigate('/research/data-sources')}>
            Get Started
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Group categories by stage for the overview
  const stageGroups = categories.reduce((acc, cat) => {
    acc[cat.stage] = (acc[cat.stage] || 0) + 1;
    return acc;
  }, {} as Record<MaturityStage, number>);

  // Sort categories: high priority actions first, then by demand score
  const sortedCategories = [...categories].sort((a, b) => {
    if (a.suggestedAction?.priority === 'high' && b.suggestedAction?.priority !== 'high') return -1;
    if (b.suggestedAction?.priority === 'high' && a.suggestedAction?.priority !== 'high') return 1;
    return (b.metrics.demandScore || 0) - (a.metrics.demandScore || 0);
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Layers className="h-5 w-5 text-indigo-500" />
              My Active Work
            </CardTitle>
            <CardDescription>
              Categories you're working on across different stages
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {/* Stage summary badges */}
            {Object.entries(stageGroups).map(([stage, count]) => {
              const config = stageConfig[stage as MaturityStage];
              return (
                <Badge
                  key={stage}
                  variant="outline"
                  className={`${config.bgColor} ${config.color} border-0`}
                >
                  <config.icon className="h-3 w-3 mr-1" />
                  {count}
                </Badge>
              );
            })}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Category cards */}
        {sortedCategories.slice(0, 6).map((category) => {
          const config = stageConfig[category.stage];
          const StageIcon = config.icon;

          return (
            <div
              key={category.id}
              className="p-4 rounded-lg border bg-card hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between gap-4">
                {/* Left: Category info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline" className={`${config.bgColor} ${config.color} border-0`}>
                      <StageIcon className="h-3 w-3 mr-1" />
                      {config.label}
                    </Badge>
                    {category.metrics.searchVolume && category.metrics.searchVolume > 10000 && (
                      <Badge variant="outline" className="bg-orange-50 text-orange-600 border-0">
                        <TrendingUp className="h-3 w-3 mr-1" />
                        High Demand
                      </Badge>
                    )}
                    {category.metrics.pendingReviews > 0 && (
                      <Badge variant="outline" className="bg-yellow-50 text-yellow-600 border-0">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        {category.metrics.pendingReviews} to review
                      </Badge>
                    )}
                  </div>
                  <h4 className="font-semibold text-base truncate" title={category.path}>
                    {category.name}
                  </h4>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">
                    {category.path}
                  </p>

                  {/* Progress bar with actual enrichment percentage */}
                  <div className="mt-3 space-y-1">
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>
                        {category.metrics.enrichedProducts.toLocaleString()}/{category.metrics.totalProducts.toLocaleString()} products enriched
                      </span>
                      <span>{category.metrics.enrichmentPercentage}% enriched</span>
                    </div>
                    <Progress
                      value={category.metrics.enrichmentPercentage}
                      className="h-1.5"
                    />
                  </div>

                  {/* Quick stats */}
                  <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Sparkles className="h-3 w-3" />
                      Stage: {config.label}
                    </span>
                    {category.metrics.hasAEOContent && (
                      <span className="flex items-center gap-1 text-green-600">
                        <FileText className="h-3 w-3" />
                        AEO Ready
                      </span>
                    )}
                    {category.metrics.hasQuestionWheel && (
                      <span className="flex items-center gap-1 text-blue-600">
                        <Eye className="h-3 w-3" />
                        Questions
                      </span>
                    )}
                  </div>
                </div>

                {/* Right: Action button */}
                <div className="flex flex-col items-end gap-2">
                  {category.suggestedAction ? (
                    <Button
                      size="sm"
                      variant={category.suggestedAction.priority === 'high' ? 'default' : 'outline'}
                      onClick={() => navigate(category.suggestedAction!.path)}
                    >
                      {category.suggestedAction.label}
                      <ArrowRight className="h-3 w-3 ml-1" />
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => navigate(`/about-business/offering?category=${encodeURIComponent(category.path)}`)}
                    >
                      <PenTool className="h-3 w-3 mr-1" />
                      Continue
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* View all link if more than 6 */}
        {categories.length > 6 && (
          <div className="text-center pt-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/about-business/offering')}>
              View all {categories.length} categories
              <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default CategoryWorkspacePanel;
